import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ticketTypeAvailable, salesStartInFuture } from './WebOrderForm';
import CalendarEventInterestForm from './CalendarEventInterestForm';
import CustomCheckoutGroupItemRow, { seatingChartItemVisible } from './CustomCheckoutGroupItemRow';

const CustomCheckoutCategories = ({
  team,
  csrfToken,
  confirm,
  adminPreview,
  ticketTypes,
  promoCode,
  updateTicketTypeQuantity,
  buildTicketReservation,
  visibleTicketTypes,
  addOns,
  ticketReservation,
  postToParent,
  formatItemsForGoogleTagManager,
  userContext,
  announceListEnabled,
  waitlistEnabled,
  checkoutCategories,
  activeCategory,
  setActiveCategory,
  addOnsChanged,
  addOnQuantityOptions,
  visibleAddOns,
  seatedAddOns,
  seatsIOPublicKey,
  selectedObjectLabel,
  selectedObjectType,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  onSelectionChange,
  no_orphan_seats,
  ticketTypesChanged,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  debouncedBuildTicketReservation,
  feeRules,
  donationTitle,
  donationCoverProcessingText,
  ticketGroups,
  findTicketGroup,
  startAtAddOns,
  membershipProducts,
  selectedMembershipProduct,
  selectedMembershipProductChanged,
  hasResaleListings,
}) => {
  const [showPresalePrompt, setShowPresalePrompt] = useState(false);
  const [availibleTickets, setAvailibleTickets] = useState([]);
  const [visibleTickets, setVisibleTickets] = useState([]);
  const [scheduledTickets, setScheduledTickets] = useState([]);
  const anyVisibleTicketTypePaused = visibleTickets.some((ticketType) => ticketType.sales_paused);

  useEffect(() => {
    setVisibleTickets(
      adminPreview ? ticketTypes : visibleTicketTypes(confirm, ticketTypes, promoCode)
    );
  }, [ticketTypes, promoCode, visibleTicketTypes, adminPreview]);

  useEffect(() => {
    setAvailibleTickets(
      adminPreview ? visibleTickets : visibleTickets.filter(ticketTypeAvailable)
    );
    setScheduledTickets(
      visibleTickets
        .filter((tt) => tt.scheduled)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    );
  }, [visibleTickets, adminPreview]);

  useEffect(() => {
    setShowPresalePrompt(confirm.preselling && Object.keys(promoCode).length === 0);
  }, [confirm, promoCode]);

  const groupHasVisibleItems = (checkoutGroup) => {
    return checkoutGroup.checkout_group_items.some((cgi) => {
      switch (cgi.item_type) {
        case 'TicketType':
          return visibleTickets.find((tt) => tt.id === cgi.item_id) !== undefined;
        case 'AddOn':
          return visibleAddOns.find((ao) => ao.id === cgi.item_id) !== undefined;
        case 'MembershipProduct':
          return (membershipProducts || []).find((mp) => mp.id === cgi.item_id) !== undefined;
        default:
          return Boolean(
            cgi.seatsio_event_id ||
            (
              cgi.add_on_seatsio_event_id &&
              (seatedAddOns || []).length > 0 &&
              (visibleAddOns || []).length > 0
            ) ||
            cgi.enable_donation ||
            (
              cgi.item_type === 'SeatingChartEvent' &&
              seatingChartItemVisible(cgi, seatedAddOns)
            )
          );
      }
    });
  };

  const showTabBar = checkoutCategories.length > 1;
  const noTicketsAvailable = availibleTickets.length === 0 && !confirm.seated_hold_present;

  return (
    <div className="cwb-public-checkout">
      {showPresalePrompt && (
        <div className="card border-0" style={{ background: '#f9f9f9', marginBottom: '15px' }}>
          <div className="card-body" style={{ paddingLeft: '15px', paddingRight: '15px' }}>
            <div className="row">
              <div className="col">
                <p className="mb-0" style={{ fontSize: '16px' }}>
                  <strong>
                    <i className="fas fa-lock text-primary mr-2" />
                    Pre-Sale Ticket Available
                  </strong>
                </p>
              </div>
              <div className="col d-flex align-items-center justify-content-end">
                <p className="mb-0" style={{ fontSize: '14px', color: '#686868' }}>
                  Enter Pre-Sale Code Above
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTabBar && (
        <ul className="nav cwb-public-tabs" style={{ marginBottom: '24px' }}>
          {checkoutCategories.map((category) => (
            <li key={category.id} className="nav-item">
              <a
                href="#"
                className={`nav-link ${activeCategory.id === category.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveCategory(category);
                }}>
                <strong>{category.name}</strong>
              </a>
            </li>
          ))}
        </ul>
      )}

      {noTicketsAvailable && !hasResaleListings ? (
        <div style={{ marginBottom: '20px' }}>
          {salesStartInFuture(confirm) || confirm.preselling ? (
            announceListEnabled ? (
              <CalendarEventInterestForm
                confirm={confirm}
                csrfToken={csrfToken}
                interestType="Announce"
              />
            ) : (
              <div className="card border-0 h-100" style={{ background: '#f9f9f9' }}>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <p className="mb-2" style={{ fontSize: '16px' }}>
                      <strong>Coming soon!</strong>
                    </p>
                    <p className="mb-0">Sales not started yet.</p>
                  </div>
                </div>
              </div>
            )
          ) : waitlistEnabled && !anyVisibleTicketTypePaused ? (
            <CalendarEventInterestForm
              confirm={confirm}
              csrfToken={csrfToken}
              interestType="Waitlist"
            />
          ) : (
            <div className="card border-0 h-100" style={{ background: '#f9f9f9' }}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2" style={{ fontSize: '16px' }}>
                    <strong>Tickets not currently available</strong>
                  </p>
                  {anyVisibleTicketTypePaused ? (
                    <p className="mb-0">Online sales for this event are paused.</p>
                  ) : scheduledTickets.length > 0 ? (
                    <>
                      <p className="mb-0">Online sales for this event are currently not available.</p>
                      <p className="mb-0">{`The next sale will begin on ${scheduledTickets[0].human_start_time}.`}</p>
                    </>
                  ) : (
                    <p className="mb-0">Online sales for this event have ended.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {checkoutCategories.map((category) =>
        category.checkout_groups.map((group) => {
          if (!groupHasVisibleItems(group)) return null;

          return (
            <div key={group.id}
                 className="cwb-public-group"
                 hidden={activeCategory.id !== category.id}>
              <div className="cwb-public-group-label">{group.name}</div>
              {group.description && group.description.length > 0 && (
                <div
                  className="cwb-public-group-desc"
                  dangerouslySetInnerHTML={{ __html: group.formatted_description }}
                />
              )}
              <div className="cwb-public-group-items">
                {group.checkout_group_items.map((checkoutGroupItem) => (
                  <CustomCheckoutGroupItemRow
                    key={checkoutGroupItem.id}
                    team={team}
                    csrfToken={csrfToken}
                    confirm={confirm}
                    adminPreview={adminPreview}
                    checkoutGroupItem={checkoutGroupItem}
                    checkoutGroupItems={group.checkout_group_items}
                    visibleTickets={visibleTickets}
                    visibleTicketTypes={visibleTicketTypes}
                    promoCode={promoCode}
                    ticketTypes={ticketTypes}
                    ticketTypesChanged={ticketTypesChanged}
                    formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                    postToParent={postToParent}
                    updateTicketTypeQuantity={updateTicketTypeQuantity}
                    buildTicketReservation={buildTicketReservation}
                    debouncedBuildTicketReservation={debouncedBuildTicketReservation}
                    addOns={addOns}
                    ticketReservation={ticketReservation}
                    userContext={userContext}
                    addOnsChanged={addOnsChanged}
                    addOnQuantityOptions={addOnQuantityOptions}
                    visibleAddOns={visibleAddOns}
                    seatedAddOns={seatedAddOns}
                    seatsIOPublicKey={seatsIOPublicKey}
                    selectedObjectLabel={selectedObjectLabel}
                    selectedObjectType={selectedObjectType}
                    seatsIOChart={seatsIOChart}
                    seatsIOChartChanged={seatsIOChartChanged}
                    seatsIOSelectedObjects={seatsIOSelectedObjects}
                    seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                    onSelectionChange={onSelectionChange}
                    no_orphan_seats={no_orphan_seats}
                    announceListEnabled={announceListEnabled}
                    waitlistEnabled={waitlistEnabled}
                    donationCustomChargeType={donationCustomChargeType}
                    donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
                    feeRules={feeRules}
                    donationTitle={donationTitle}
                    donationCoverProcessingText={donationCoverProcessingText}
                    startAtAddOns={startAtAddOns}
                    membershipProducts={membershipProducts}
                    selectedMembershipProduct={selectedMembershipProduct}
                    selectedMembershipProductChanged={selectedMembershipProductChanged}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

CustomCheckoutCategories.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  adminPreview: PropTypes.bool,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  updateTicketTypeQuantity: PropTypes.func,
  buildTicketReservation: PropTypes.func,
  visibleTicketTypes: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  postToParent: PropTypes.func,
  formatItemsForGoogleTagManager: PropTypes.func,
  userContext: PropTypes.object,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  checkoutCategories: PropTypes.array.isRequired,
  activeCategory: PropTypes.object,
  setActiveCategory: PropTypes.func.isRequired,
  addOnsChanged: PropTypes.func,
  addOnQuantityOptions: PropTypes.func,
  visibleAddOns: PropTypes.array,
  seatedAddOns: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  selectedObjectLabel: PropTypes.func,
  selectedObjectType: PropTypes.func,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func,
  onSelectionChange: PropTypes.func,
  no_orphan_seats: PropTypes.bool,
  ticketTypesChanged: PropTypes.func,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func,
  debouncedBuildTicketReservation: PropTypes.func,
  feeRules: PropTypes.array,
  donationTitle: PropTypes.string,
  donationCoverProcessingText: PropTypes.string,
  ticketGroups: PropTypes.object,
  findTicketGroup: PropTypes.func,
  startAtAddOns: PropTypes.bool,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  selectedMembershipProductChanged: PropTypes.func,
  hasResaleListings: PropTypes.bool,
};

CustomCheckoutCategories.defaultProps = {
  adminPreview: null,
  promoCode: null,
  updateTicketTypeQuantity: null,
  buildTicketReservation: null,
  addOns: null,
  ticketReservation: null,
  postToParent: null,
  formatItemsForGoogleTagManager: null,
  userContext: null,
  announceListEnabled: null,
  waitlistEnabled: null,
  activeCategory: null,
  addOnsChanged: null,
  addOnQuantityOptions: null,
  visibleAddOns: null,
  seatedAddOns: null,
  seatsIOPublicKey: null,
  selectedObjectLabel: null,
  selectedObjectType: null,
  seatsIOChart: null,
  seatsIOChartChanged: null,
  seatsIOSelectedObjects: null,
  seatsIOSelectedObjectsChanged: null,
  onSelectionChange: null,
  no_orphan_seats: null,
  ticketTypesChanged: null,
  donationCustomChargeType: null,
  donationCustomChargeTypeChanged: null,
  debouncedBuildTicketReservation: null,
  feeRules: null,
  donationTitle: null,
  donationCoverProcessingText: null,
  ticketGroups: null,
  findTicketGroup: null,
  startAtAddOns: null,
  membershipProducts: null,
  selectedMembershipProduct: null,
  selectedMembershipProductChanged: null,
  hasResaleListings: false,
};

export default CustomCheckoutCategories;
